import { useEffect, useState, createContext, ReactNode } from 'react'
import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { useEffectOnCookieImport } from '../cookie'
// Imported from the modules themselves rather than the `utils/index` barrel:
// the barrel is declared as an untyped ambient module (src/types/index.d.ts),
// so everything coming through it is `any`.
import { getMobileAppInfo, SessionInfo } from 'utils/helpers'
import { authenticatedUserFragment } from 'utils/auth-utils'
import { resetSessionTimer, clearSessionTimer } from 'utils/session-timer'

const CURRENT_USER = gql`
  query CurrentUser {
    currentUser {
      ...userFragment
    }
  }
  ${authenticatedUserFragment}
`

const defaultState: SessionInfo = {
  mobileApp: false,
  mobileWeb: false,
  version: 0,
  platform: 'web',
  isAuthenticated: false,
  authLoading: true,
  user: null,
  setUser: (_user: any) => {},
  logIn: (_user: any) => {},
  logOut: () => {},
  mobileAppFromV: (_minVersion: number) => true
}

// Not Partial: every consumer assumes the provider is mounted, and
// `utils/helpers` already declares its session helpers as
// `React.Context<SessionInfo>`. Before this file was typed, the unresolved
// `SessionInfo` name degraded to `any` and none of that was checked.
export const SessionContext = createContext<SessionInfo>(defaultState)

export const SessionProvider = (props: Props) => {
  const [userOverride, setUserOverride] = useState<any>(null)
  const info = getMobileAppInfo()
  const { data, loading, refetch } = useQuery<any>(CURRENT_USER, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  })

  const user = userOverride || data?.currentUser || null
  const isAuthenticated = !!user
  const authLoading = loading && !user

  const mobileAppFromV = (minVersion: number): boolean => {
    if (info.mobileApp) return info.version >= (minVersion || 1)
    return false
  }

  useEffect(() => {
    if (loading) return

    if (user) {
      resetSessionTimer()
    } else {
      clearSessionTimer()
    }
  }, [user, loading])

  useEffectOnCookieImport(() => {
    // If cookies were imported from another domain, re-query current user.
    refetch()
  })

  return (
    <SessionContext.Provider
      value={{
        ...info,
        ...{ isAuthenticated, user, authLoading },
        logIn: (user: any) => {
          setUserOverride(user)
        },
        logOut: () => {
          clearSessionTimer()
          setUserOverride(null)
        },
        setUser: (user: any) => {
          setUserOverride(user)
        },
        mobileAppFromV
      }}
    >
      {props.children}
    </SessionContext.Provider>
  )
}

interface Props {
  children: ReactNode
}
